import { Component, Inject, OnDestroy } from '@angular/core';
import { NgClass } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatDialogRef, MatDialog, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatRippleModule } from '@angular/material/core';
import { Router } from '@angular/router';
import { finalize, Subject, take, takeUntil } from 'rxjs';
import { BontonPanel } from 'environments/bonton-panel';
import { CommonUtils } from 'app/utils/commonutils';
import { cache_key } from 'app/utils/const';
import { ForexService } from 'app/services/forex.service';
import { WalletService } from 'app/services/wallet.service';
import { EntityService } from 'app/services/entity.service';
import { ToasterService } from 'app/services/toaster.service';
import { AuthService } from 'app/core/auth/auth.service';
import { PaymentService } from 'app/services/payment.service';
import Swal from 'sweetalert2';
import { CacheService } from 'app/services/cache.service';
import { CommanService } from 'app/services/comman.service';
import { InquiryThankComponent } from 'app/modules/common-component/inquiry-thank/inquiry-thank.component';

@Component({
  selector: 'app-forex-inquiry',
  standalone: true,
  imports: [
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatDialogModule,
    MatCheckboxModule,
    MatRadioModule,
    MatRippleModule
],
  templateUrl: './forex-inquiry.component.html',
  styleUrls: ['./forex-inquiry.component.scss']
})
export class ForexInquiryComponent implements OnDestroy {
  // ---- lifecycle / subscriptions ----
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  // ---- dialog input & display model ----
  record: any = {};
  tokan_amount_disp: any;

  // ---- form & UI state ----
  formGroup: FormGroup;
  isPay = false;
  isNote = true;
  disableBtn = false;

  // ---- auth / mode ----
  isLoggedin = false;
  bontonPanel: any = BontonPanel;

  // ---- payment model ----
  payment_method_online: boolean = true;
  payment_method_wallet: boolean = true;
  paymentMethods: any[] = [];
  paymentModesList: any[] = [];
  paymentMode: any = null;
  paymentDescriptionCharacterLimit = 70;
  isPartialAvailable: boolean = false;
  mainPayment: string | null = null;
  PaymentValidateModel = {
    is_partial_available: false,
    mainbalance: 0,
    chooseOne: false,
    chooseTwo: false,
    payment_method: '',
    grandtotal: 0,
    walletdisable: false,
    creditBalance: 0,
    walletBalance: 0
  };

  /**
   * Construct the ForexInquiryComponent dialog.
   * Initializes record data, pre-computes token display amount (if applicable),
   * and subscribes to login state.
   *
   * @param {MatDialogRef<ForexInquiryComponent>} matDialogRef - Reference to this dialog instance.
   * @param {FormBuilder} builder - Builds and manages the reactive form.
   * @param {MatDialog} matDialog - Opens additional dialogs (thank-you, etc.).
   * @param {Router} router - Handles navigation after success/failure.
   * @param {ForexService} forexService - API client for forex inquiry creation.
   * @param {WalletService} walletService - Fetches wallet/credit balances and dynamic payment modes.
   * @param {EntityService} entityService - Emits entity-level events (e.g., balance changed).
   * @param {ToasterService} alertService - Shows toast notifications for errors/info.
   * @param {AuthService} authService - Provides auth state and opens sign-in dialog.
   * @param {PaymentService} paymentService - Wraps integrations for various payment gateways.
   * @param {CacheService} cacheService - For clearing cached keys after payments.
   * @param {CommanService} commanService - Toggles global loader UI states.
   * @param {*} data - Dialog input data; expected to contain forex details & payment config.
   * @returns {void}
   */
  constructor(
    public matDialogRef: MatDialogRef<ForexInquiryComponent>,
    private builder: FormBuilder,
    private matDialog: MatDialog,
    private router: Router,
    private forexService: ForexService,
    private walletService: WalletService,
    private entityService: EntityService,
    private alertService: ToasterService,
    private authService: AuthService,
    private paymentService: PaymentService,
    private cacheService: CacheService,
    private commanService: CommanService,
    @Inject(MAT_DIALOG_DATA) public data: any = {}
  ) {
    this.record = data;

    // Precompute token display amount if token flow is enabled
    if (this.record.payment_mode === 'Inquiry With Token Amount') {
      this.tokan_amount_disp = (this.record.token_type === '% of total amount')
          ? (this.record.token_amount * this.record.forex_amount) / 100
          : this.record.token_amount;
    }

    // Subscribe to login state
    this.authService.loggedIn$.pipe(takeUntil(this._unsubscribeAll)).subscribe(res => {
      this.isLoggedin = res;
    });
  }

  /**
   * Initialize form, load payment methods/modes & balances (B2B),
   * and compute initial totals/flags.
   *
   * @returns {void}
   */
  ngOnInit(): void {
    // Build lead form
    this.formGroup = this.builder.group({
      lead_name: ['', Validators.required],
      lead_email: ['', [Validators.required, Validators.email]],
      lead_mobile: ['', [Validators.required, Validators.minLength(7)]],
    });

    // Load dynamic payment modes (UPI / Net banking / Cards / etc.)
    this.walletService.getDynamicPaymentModes({}).subscribe({
      next: (data) => {
        if (data?.length > 0) {
          this.paymentModesList = data;
          this.paymentMode = data[0];
        } else {
          this.paymentModesList = [];
          this.paymentMode = null;
        }
      },
    });

    // Load allowed payment method set (e.g., ['Wallet','Online'])
    this.walletService.getPaymentTypeModes({ type: '' }).subscribe({
      next: (data) => (this.paymentMethods = data ?? []),
    });

    // For B2B, compute wallet + credit balances first, then totals
    if (this.bontonPanel.isB2B()) {
      this.walletService.WalletBalance().subscribe({
        next: (result) => {
          this.PaymentValidateModel.walletBalance = Math.max(0, result.balance);
          this.walletService.getWalletCreditBalance().subscribe({
            next: (response) => {
              this.PaymentValidateModel.creditBalance = response.balance;
              this.PaymentValidateModel.mainbalance = this.PaymentValidateModel.creditBalance + this.PaymentValidateModel.walletBalance;
              this.PaymentValidateModel.is_partial_available = this.isPartialAvailable;
              this.calculateGrandTotal();
            },
            error: (err) => this.alertService.showToast('error', err),
          });
        },
        error: (err) => this.alertService.showToast('error', err),
      });
    } else {
      // B2C: just compute totals and set default method
      this.calculateGrandTotal();
    }
  }

  /**
   * Compute final payable total (delegates to totalChanged for rules).
   *
   * @returns {void}
   */
  calculateGrandTotal(): void {
    this.PaymentValidateModel.grandtotal = 0;
    this.totalChanged();
  }

  /**
   * Mark the note as acknowledged and reveal the form.
   *
   * @returns {void}
   */
  isAgree(): void {
    this.isNote = false;
  }

  /**
   * Normalize/validate selected payment method flags
   * and set defaults for B2C (Online only).
   *
   * @returns {void}
   */
  totalChanged(): void {
    this.PaymentValidateModel = CommonUtils.ValidatePaymentMethods(this.PaymentValidateModel);

    if (this.bontonPanel.isB2C()) {
      // B2C: force Online
      this.PaymentValidateModel.payment_method = 'Online';
      this.PaymentValidateModel.chooseOne = true;
      this.PaymentValidateModel.chooseTwo = false;
    } else {
      // B2B: if not both are available, default to the first
      if (!(this.paymentMethods?.includes('Online') && this.paymentMethods?.includes('Wallet'))) {
        this.PaymentValidateModel.payment_method = this.paymentMethods[0];
        this.payment_method_online = this.paymentMethods[0] === 'Online';
        this.PaymentValidateModel.chooseOne = true;
        this.PaymentValidateModel.chooseTwo = false;
      }
    }
  }

  /**
   * Manually refresh wallet balance (triggered by a refresh icon).
   *
   * @returns {void}
   */
  refresh(): void {
    this.walletService.WalletBalance().subscribe({
      next: (res) => {
        this.PaymentValidateModel.walletBalance = res.balance;
        this.entityService.raiseBalanceChanged();
      },
      error: () => this.alertService.showToast('error', ''),
    });
  }

  /**
   * Select the currently chosen online payment mode (UPI, Net banking, etc.).
   *
   * @param {any} mode - Mode object returned from getDynamicPaymentModes.
   * @returns {void}
   */
  selectPaymentMode(mode: any): void {
    this.paymentMode = mode;
  }

  /**
   * Allow only digits in the mobile number field.
   *
   * @param {KeyboardEvent} event - Keypress event.
   * @returns {boolean} True to allow, false to block.
   */
  numberOnly(event: KeyboardEvent): boolean {
    const key = (event.which ? event.which : (event as any).keyCode);
    return !(key > 31 && (key < 48 || key > 57));
  }

  /**
   * Close the payment panel and go back to the form step.
   *
   * @returns {void}
   */
  payClose(): void {
    this.isPay = false;
  }

  /**
   * Derive the main payment type from the current toggle state.
   *
   * @returns {('Wallet'|'Online'|'Partial'|null)} Payment type or null when unset.
   */
  getMainPaymentType(): 'Wallet' | 'Online' | 'Partial' | null {
    if (this.PaymentValidateModel.chooseOne) {
      return this.PaymentValidateModel.payment_method as any;
    } else if (this.PaymentValidateModel.chooseTwo) {
      const { payment_method_wallet, payment_method_online } = this;
      if (payment_method_wallet && !payment_method_online) return 'Wallet';
      if (payment_method_online && !payment_method_wallet) return 'Online';
      if (payment_method_wallet && payment_method_online) return 'Partial';
    }
    return null;
  }

  /**
   * Build the request payload for inquiry/payment APIs from the form + record.
   *
   * @returns {any} Payload object to send to the server.
   */
  prepareFormData(): any {
    const formData = { ...this.formGroup.value };

    Object.assign(formData, {
      id: this.record.id,
      forex_type: this.record.forex_type,
      city_id: this.record.city_id,
      transaction_type: this.record.transaction_type,
      from_currency_id: this.record.from_currency_id,
      to_currency_id: this.record.to_currency_id,
      forex_rate: this.record.forex_rate,
      forex_amount: this.record.forex_amount,
      to_amount: this.record.to_amount,
      lead_from: this.record.lead_from,
      payment_type: this.mainPayment,
      payment_mode: this.mainPayment === 'Wallet' ? '' : this.paymentMode?.mode,
    });

    // Attach PG mode id when Online/Partial
    if (this.mainPayment?.toLowerCase() === 'online' || this.mainPayment?.toLowerCase() === 'partial') {
      formData['PspModeId'] = this.paymentMode?.id;
    }

    return formData;
  }

  /**
   * Submit inquiry:
   * - If B2C and not logged in, open sign-in dialog.
   * - Validate form, then either create inquiry (Inquiry Only) or continue to payment step.
   *
   * @returns {void}
   */
  submit(): void {
    Object.keys(this.formGroup.controls).forEach(key => {
      const control = this.formGroup.get(key);
      if (control && typeof control.value === 'string') {
        control.setValue(control.value.trim());
      }
    });

    // Require sign-in for B2C users
    if (!this.isLoggedin && this.bontonPanel.isB2C()) {
      this.authService.openSignInDialog();
      return;
    }

    // Validate form
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this.isPay = true;
    this.mainPayment = this.getMainPaymentType();
    const payload = this.prepareFormData();

    this.disableBtn = true;
    this.commanService.raiseLoader(true);

    if (this.record?.payment_mode === 'Inquiry Only') {
      // Pure inquiry – no payment step
      this.isPay = false;

      this.forexService.create(payload)
        .pipe(
          take(1),
          finalize(() => {
            // Always re-enable UI + hide loader after request
            this.disableBtn = false;
            this.commanService.raiseLoader(false);
          })
        )
        .subscribe({
          next: () => {
            this.matDialogRef.close(true);
            // Thank-you dialog
            this.matDialog.open(InquiryThankComponent, {
              data: 'Forex',
              panelClass: 'full-width-dialog',
              disableClose: true,
            });
          },
          error: (err) => this.alertService.showToast('error', err),
        });

      return;
    }

    // Proceed to payment step (UI only)
    this.commanService.raiseLoader(false);
    this.disableBtn = false;
  }


  /**
   * Execute payment flow:
   * - Create inquiry
   * - If online, invoke the correct payment gateway helper
   * - Handle success/failure, clean cache, navigate and close dialog
   *
   * @returns {void}
   */
  payment(): void {
    this.disableBtn = true;

    this.mainPayment = this.getMainPaymentType();
    const formData = this.prepareFormData();
    this.commanService.raiseLoader(true);

    this.forexService.create(formData).subscribe({
      next: (res: any) => {
        this.commanService.raiseLoader(false);

        // If server signals online PG handoff, trigger handler and close
        if (res && res.data?.is_online) {
          this.cacheService.remove(cache_key.my_recharge);

          switch (res.data.pg) {
            case 'CashFree':    this.paymentService.cashFreePaymentMode(res.data.appURL); break;
            case 'EaseBuzz':    this.paymentService.easeBuzzPaymentMode(res.data.session_id, res.data.mKey, res.data.env, 'forex'); break;
            case 'PayU':        this.paymentService.payUPaymentModel(res.data.appURL); break;
            case 'CCAvenue':    this.paymentService.CCAvenuePaymentModel(res.data.appURL); break;
            case 'RazorPay':    this.paymentService.razorPayPaymentModel(res.data.appURL); break;
            case 'CBPay':       this.paymentService.CBPayPaymentModel(res.data.appURL); break;
            case 'QuickTeller': this.paymentService.quickTellerPaymentModel(res.data.appURL); break;
          }
          this.matDialogRef.close(true);

        }

        if (res.data && res.data['status']) {
          this.matDialog.open(InquiryThankComponent, {
            data: 'Forex',
            panelClass: 'full-width-dialog',
            disableClose: true,
          });
          this.router.navigate(['/forex']);
          this.matDialogRef.close(true);
        } else {
          Swal.fire('Payment Fail!', res.data.fail_reason, 'error');
          this.router.navigate(['/forex']);
          this.matDialogRef.close(true);
        }

        this.disableBtn = true;
      },
      error: (err) => {
        Swal.fire('Payment Fail!', err, 'error');
        this.router.navigate(['/forex']);
        this.matDialogRef.close(true);
        this.commanService.raiseLoader(false);
        this.disableBtn = true;
      }
    });
  }

  /**
   * Toggle long/short description per payment mode card.
   *
   * @param {any} mode - Current payment mode object.
   * @returns {void}
   */
  toggleDescription(mode: any): void {
    mode.showFullDescription = !mode.showFullDescription;
  }

  /**
   * Complete subscriptions to avoid memory leaks.
   *
   * @returns {void}
   */
  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }
}
